/**
 * TeamBoxesScene (key 'TeamBoxes') — the team + boxes home, one cozy screen with two
 * tabs (contract: Team and Boxes are one combined scene).
 *
 *  • Team: the active party (up to 6) as soft cards — sprite, nickname/level, HP bar,
 *    types. Tap a card to open a detail panel (full computed stats, nature, ability,
 *    moves + PP). Reorder with gentle ◀ ▶ controls; from detail you can send a party
 *    mon to a box (never below 1 in the party — no way to soft-lock yourself).
 *  • Boxes: every stored mon in a searchable, touch-drag ScrollList. Tap a row to see
 *    detail; withdraw to the team (if there's room) from there.
 *
 * Calm + touch-first + responsive throughout (Layout + the cozy UI kit). All truth
 * comes from the GameSession; this scene only presents + asks the session to mutate
 * (addToTeam / depositToBox), then persists via session.save(). No pressure framing.
 */
import Phaser from 'phaser';
import type { PokemonInstance, SpeciesData, PokemonType } from '@/shared';
import { computeStats, getNature, NATURES, expForLevel } from '@/core';
import type { GameSession } from '@/session';
import { COLORS, CSS, FONT, SPACING } from '../ui/theme.js';
import { Panel } from '../ui/Panel.js';
import { Button } from '../ui/Button.js';
import { ScrollList } from '../ui/ScrollList.js';
import { TextInput } from '../ui/TextInput.js';
import { FocusRing } from '../ui/FocusRing.js';
import { moveGrid, type GridDir } from '../ui/gridNav.js';
import { domTextFieldFocused } from '../ui/keyboardGate.js';
import { Toast } from '../ui/Toast.js';
import { Layout } from '../layout/index.js';
import type { SpriteResolver } from '../sprites/index.js';
import { SpriteResolver as SpriteResolverImpl } from '../sprites/index.js';
import { fadeIn, goTo, isReducedMotion } from '../flow.js';
import { REGISTRY_SPRITES } from '../PreloadScene.js';
import { getSession } from '../session.js';
import { typePill, showSpecies, calmBackdrop, redrawBackdrop } from './parts.js';

type Tab = 'team' | 'boxes';

const STAT_LABEL: Record<string, string> = {
  hp: 'HP',
  atk: 'Attack',
  def: 'Defense',
  spatk: 'Sp. Atk',
  spdef: 'Sp. Def',
  spe: 'Speed',
};
const STAT_ORDER = ['hp', 'atk', 'def', 'spatk', 'spdef', 'spe'] as const;

export class TeamBoxesScene extends Phaser.Scene {
  private layout!: Layout;
  private session!: GameSession;
  private resolver!: SpriteResolver;
  private reduced = false;

  private backdrop!: Phaser.GameObjects.Graphics;
  private heading?: Phaser.GameObjects.Text;
  private tabButtons: { tab: Tab; button: Button }[] = [];
  private backButton?: Button;

  private tab: Tab = 'team';
  /** Everything inside the active tab lives here so a swap is a clean wipe. */
  private body?: Phaser.GameObjects.Container;
  /** The modal detail overlay, when open. */
  private detail?: Phaser.GameObjects.Container;

  /** Boxes search query (lives across relayouts within the Boxes tab). */
  private boxQuery = '';

  // ── keyboard nav ────────────────────────────────────────────────────────────
  /** The focusable cards/rows in the active tab, in visual order. */
  private navCells: {
    container: Phaser.GameObjects.Container;
    mon: PokemonInstance;
    source: 'team' | 'box';
    cy: number;
    height: number;
    width: number;
  }[] = [];
  private navCols = 1;
  private navList?: ScrollList;
  private navListViewH = 0;
  private navCursor = -1;
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;

  constructor() {
    super('TeamBoxes');
  }

  init(data?: { tab?: Tab }): void {
    this.tab = data?.tab === 'boxes' ? 'boxes' : 'team';
  }

  create(): void {
    // Phaser REUSES this scene instance, so create() re-runs on every RE-ENTRY (Town →
    // TeamBoxes → Town → TeamBoxes). The tabButtons array survives between visits, so the
    // create loop would PUSH a second pair onto the stale one (and highlightTabs() would
    // then iterate destroyed buttons). resetState() clears the arrays/handles first.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.session = getSession(this);
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolverImpl(this);
    this.reduced = isReducedMotion();

    this.backdrop = calmBackdrop(this, this.layout);

    this.heading = this.add
      .text(0, 0, 'Your Pokémon', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    for (const tab of ['team', 'boxes'] as Tab[]) {
      const button = new Button(this, 0, 0, {
        label: tab === 'team' ? 'Team' : 'Boxes',
        variant: 'ghost',
        width: 120,
        reducedMotion: this.reduced,
        onClick: () => this.switchTab(tab),
      });
      this.tabButtons.push({ tab, button });
    }

    this.backButton = new Button(this, 0, 0, {
      label: '‹ Town',
      variant: 'ghost',
      width: 110,
      reducedMotion: this.reduced,
      onClick: () => goTo(this, 'Town'),
    });

    this.focusRing = new FocusRing(this, { depth: 90 });

    // relayout() positions the chrome and (re)builds the active tab's body.
    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  /**
   * Full keyboard nav: arrows move the highlight across the active tab's cards/rows
   * (auto-scrolling to keep the cursor visible), Enter opens the focused Pokémon's detail,
   * Tab or [ / ] switch tabs, Esc closes an open detail else returns to Town. Ignored
   * while a DOM text field (the box search) is focused. Cleaned up on SHUTDOWN.
   */
  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      // With a detail card open, Esc closes it; other keys are swallowed underneath.
      if (this.detail) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeDetail();
        }
        return;
      }
      // Tab / bracket keys switch tabs regardless of cursor state.
      if (e.key === 'Tab' || e.key === '[' || e.key === ']') {
        e.preventDefault();
        this.switchTab(this.tab === 'team' ? 'boxes' : 'team');
        return;
      }
      const dirs: Record<string, GridDir> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        s: 'down',
        a: 'left',
        d: 'right',
      };
      const dir = dirs[e.key];
      if (dir) {
        const n = this.navCells.length;
        if (n > 0) {
          e.preventDefault();
          this.setNavCursor(
            this.navCursor < 0 ? 0 : moveGrid(this.navCursor, dir, n, this.navCols),
          );
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (this.navCursor >= 0 && this.navCells[this.navCursor]) {
          e.preventDefault();
          const cell = this.navCells[this.navCursor]!;
          this.openDetail(cell.mon, cell.source);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        goTo(this, 'Town');
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  /** Seat the keyboard cursor on a card/row: auto-scroll it into view + draw the ring. */
  private setNavCursor(index: number): void {
    const n = this.navCells.length;
    if (n === 0 || !this.navList) {
      this.focusRing?.hide();
      return;
    }
    this.navCursor = Math.max(0, Math.min(n - 1, index));
    const cell = this.navCells[this.navCursor];
    if (!cell) return;
    const top = cell.cy - cell.height / 2;
    const bottom = cell.cy + cell.height / 2;
    const off = this.navList.scrollOffset;
    const pad = SPACING.sm;
    if (top < off) this.navList.scrollTo(top - pad);
    else if (bottom > off + this.navListViewH)
      this.navList.scrollTo(bottom - this.navListViewH + pad);
    const m = cell.container.getWorldTransformMatrix();
    this.focusRing?.show(m.tx, m.ty, cell.width, cell.height);
    this.focusRing?.bringToTop();
  }

  /**
   * Drop every reference a prior visit's create() populated so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * arrays/handles so nothing stacks. Safe on a first entry (everything already empty).
   * (`backdrop` is non-optional and is reassigned in create() before use, so it stays.)
   */
  private resetState(): void {
    this.tabButtons = [];
    this.heading = undefined;
    this.backButton = undefined;
    this.body = undefined;
    this.detail = undefined;
    this.teamList = undefined;
    this.boxList = undefined;
    this.navCells = [];
    this.navList = undefined;
    this.navCursor = -1;
    this.focusRing = undefined;
    this.keyHandler = undefined;
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave nothing behind: kill
   * tweens/timers, destroy the backdrop/heading/buttons + the active body + any open
   * detail overlay (containers cascade to children), and drop the array references.
   */
  private teardown(): void {
    this.tweens.killAll();
    this.time.removeAllEvents();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    this.navCells = [];
    this.navList = undefined;
    for (const { button } of this.tabButtons) button.destroy();
    this.tabButtons = [];
    this.backdrop?.destroy();
    this.heading?.destroy();
    this.backButton?.destroy();
    this.body?.destroy();
    this.detail?.destroy();
    this.heading = undefined;
    this.backButton = undefined;
    this.body = undefined;
    this.detail = undefined;
  }

  // ── tab plumbing ────────────────────────────────────────────────────────────

  private switchTab(tab: Tab): void {
    if (tab === this.tab) return;
    this.tab = tab;
    this.navCursor = -1; // a fresh tab starts unfocused
    this.closeDetail();
    this.renderTab();
  }

  private renderTab(): void {
    this.body?.destroy();
    this.body = this.add.container(0, 0);
    this.navCells = [];
    this.navList = undefined;
    this.highlightTabs();
    if (this.tab === 'team') this.buildTeam();
    else this.buildBoxes();
    this.positionBody();
    // Re-seat the keyboard focus ring on the (rebuilt) focused card, if any.
    if (this.navCursor >= 0 && this.navCells.length > 0) {
      this.setNavCursor(Math.min(this.navCursor, this.navCells.length - 1));
    } else {
      this.focusRing?.hide();
    }
  }

  private highlightTabs(): void {
    for (const { tab, button } of this.tabButtons) {
      button.setAlpha(tab === this.tab ? 1 : 0.55);
    }
  }

  /**
   * The y where the tab content begins (under the header chrome). Computed by relayout()
   * — which stacks the header differently on a phone vs desktop — so the body always
   * starts cleanly below whatever header height the current viewport produced.
   */
  private headerBottom = 0;
  private get contentTop(): number {
    return this.headerBottom;
  }

  // ── TEAM tab ──────────────────────────────────────────────────────────────────

  private buildTeam(): void {
    const team = this.session.getTeam();
    if (!this.body) return;

    if (team.length === 0) {
      const note = this.add
        .text(this.layout.centerX, this.layout.centerY, 'No Pokémon with you yet — that’s okay.', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.textMuted,
        })
        .setOrigin(0.5);
      this.body.add(note);
      return;
    }

    const safe = this.layout.safe;
    const cardW = Math.min(360, safe.width - SPACING.lg * 2);
    const cardH = 96;
    const cols = this.layout.isCompact ? 1 : safe.width >= cardW * 2 + SPACING.lg * 3 ? 2 : 1;
    const gap = SPACING.md;
    const gridW = cols * cardW + (cols - 1) * gap;
    const rows = Math.ceil(team.length / cols);
    const gridH = rows * cardH + (rows - 1) * gap;

    // A full team of 6 in a single phone column is taller than the safe area (and can be
    // clipped under the home-indicator inset), so the cards go in a touch-drag ScrollList
    // bounded to the area below the tab row — every party member stays reachable. (On a
    // roomy screen the grid fits; the list just doesn't scroll.)
    const top = this.contentTop;
    const listH = Math.max(cardH, safe.y + safe.height - top - SPACING.lg);
    const cx = this.layout.centerX;
    const list = new ScrollList(this, cx, top + listH / 2, {
      width: Math.max(gridW + SPACING.md, cardW + SPACING.md),
      height: listH,
    });
    this.teamList = list;
    this.body.add(list);
    // Wire keyboard nav to this grid.
    this.navList = list;
    this.navListViewH = listH;
    this.navCols = cols;

    // Center the grid horizontally within the list's content (content cx === list cx).
    const startX = -gridW / 2 + cardW / 2;
    // ScrollList maps content-local y=0 to the viewport TOP, so lay cards out from the
    // top down (with a small top pad) — the first card sits at the top on open and the
    // rest are reachable by scrolling.
    const topPad = SPACING.sm;
    team.forEach((mon, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gap);
      const y = topPad + cardH / 2 + row * (cardH + gap);
      const card = this.teamCard(mon, i, team.length, cardW, cardH);
      card.setPosition(x, y);
      list.content.add(card);
      this.navCells.push({
        container: card,
        mon,
        source: 'team',
        cy: y,
        height: cardH,
        width: cardW,
      });
    });
    list.setContentHeight(topPad + gridH + SPACING.sm);
    list.scrollTo(0);
  }

  private teamCard(
    mon: PokemonInstance,
    index: number,
    teamSize: number,
    w: number,
    h: number,
  ): Phaser.GameObjects.Container {
    const species = this.speciesOf(mon);
    const c = this.add.container(0, 0);

    const panel = new Panel(this, 0, 0, { width: w, height: h, fill: COLORS.surface });
    c.add(panel);

    const sprite = this.add.image(-w / 2 + SPACING.lg + 28, 0, '__MISSING').setVisible(false);
    c.add(sprite);
    if (species) void showSpecies(this, this.resolver, species.dexNo, sprite, 64);

    const textX = -w / 2 + SPACING.lg + 64;
    const name = this.add.text(textX, -h / 2 + SPACING.md, this.displayName(mon, species), {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.text,
      fontStyle: 'bold',
    });
    const lvl = this.add.text(textX, -h / 2 + SPACING.md + FONT.size.md + 4, `Lv ${mon.level}`, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.textMuted,
    });
    c.add([name, lvl]);

    // HP bar — calm, never alarming. It lives in a clean lane: from the text column
    // to a reserved right gutter for the reorder controls, with room for its own
    // "cur/max" label so nothing draws full-width across the card.
    const rightGutter = teamSize > 1 ? 44 : SPACING.lg;
    const labelRoom = 48; // space the "cur/max" label needs to the bar's right
    const barX = textX;
    const barW = Math.max(48, w / 2 - rightGutter - labelRoom - barX);
    const barY = h / 2 - SPACING.lg;
    c.add(this.hpBar(barX, barY, barW, mon.currentHp, mon.maxHp));

    // Types, top-right.
    if (species) {
      let tx = w / 2 - SPACING.md;
      for (let i = species.types.length - 1; i >= 0; i--) {
        const pill = typePill(this, 0, -h / 2 + SPACING.md + 6, species.types[i] as PokemonType);
        const pw = pill.width;
        pill.x = tx - pw / 2;
        tx -= pw + SPACING.xs;
        c.add(pill);
      }
    }

    // A gentle "walks with you" badge on the current follower (the team mon trailing
    // the player in the overworld). Calm, never a demand — just a warm marker.
    if (this.isFollower(mon)) {
      const badge = this.add
        .text(textX, h / 2 - SPACING.lg - 14, '✿ walks with you', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.accent,
        })
        .setOrigin(0, 0.5);
      c.add(badge);
    }

    // Reorder controls (gentle ◀ ▶), only meaningful with >1 mon.
    if (teamSize > 1) {
      const up = this.tinyButton('▲', () => this.reorder(index, index - 1), index === 0);
      const down = this.tinyButton(
        '▼',
        () => this.reorder(index, index + 1),
        index === teamSize - 1,
      );
      up.setPosition(w / 2 - SPACING.md, h / 2 - SPACING.lg - 14);
      down.setPosition(w / 2 - SPACING.md, h / 2 - SPACING.sm);
      c.add([up, down]);
    }

    // Tap the card body to open detail.
    panel.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    panel.input!.cursor = 'pointer';
    // Open on TAP (POINTER_UP); a drag-scroll of the team list no-ops here.
    panel.on(Phaser.Input.Events.POINTER_UP, () => {
      if (!this.teamList?.wasDragging()) this.openDetail(mon, 'team');
    });

    return c;
  }

  private hpBar(
    x: number,
    y: number,
    w: number,
    current: number,
    max: number,
  ): Phaser.GameObjects.Container {
    const h = 7;
    const frac = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
    const g = this.add.graphics();
    g.fillStyle(COLORS.surfaceAlt, 1);
    g.fillRoundedRect(0, -h / 2, w, h, h / 2);
    // Soft sage when healthy, soft apricot lower, soft rose when very low — never red.
    const col = frac > 0.5 ? COLORS.accent : frac > 0.2 ? COLORS.warm : COLORS.rose;
    g.fillStyle(col, 1);
    g.fillRoundedRect(0, -h / 2, Math.max(2, w * frac), h, h / 2);
    const label = this.add
      .text(w + SPACING.sm, 0, `${current}/${max}`, {
        fontFamily: FONT.mono,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textFaint,
      })
      .setOrigin(0, 0.5);
    return this.add.container(x, y, [g, label]);
  }

  private tinyButton(
    glyph: string,
    onClick: () => void,
    disabled: boolean,
  ): Phaser.GameObjects.Container {
    const r = 13;
    const g = this.add.graphics();
    g.fillStyle(COLORS.surfaceAlt, 1);
    g.fillCircle(0, 0, r);
    const t = this.add
      .text(0, 0, glyph, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: disabled ? CSS.textFaint : CSS.text,
      })
      .setOrigin(0.5);
    const c = this.add.container(0, 0, [g, t]);
    c.setSize(r * 2, r * 2);
    c.setAlpha(disabled ? 0.4 : 1);
    if (!disabled) {
      c.setInteractive(new Phaser.Geom.Circle(0, 0, r), Phaser.Geom.Circle.Contains);
      c.input!.cursor = 'pointer';
      c.on(Phaser.Input.Events.POINTER_DOWN, onClick);
    }
    return c;
  }

  private async reorder(from: number, to: number): Promise<void> {
    this.session.reorderTeam(from, to);
    await this.session.save();
    this.closeDetail();
    this.renderTab();
  }

  // ── BOXES tab ──────────────────────────────────────────────────────────────────

  private buildBoxes(): void {
    if (!this.body) return;
    const safe = this.layout.safe;
    const listW = Math.min(440, safe.width - SPACING.lg * 2);
    const cx = this.layout.centerX;

    // Search field.
    const searchY = this.contentTop + SPACING.md;
    const search = new TextInput(this, cx, searchY, {
      width: listW,
      placeholder: 'search your boxes…',
      maxLength: 24,
      onChange: (v) => {
        this.boxQuery = v;
        this.refreshBoxList();
      },
    });
    if (this.boxQuery) search.setValue(this.boxQuery);
    this.body.add(search);

    const listTop = searchY + SPACING.xl + SPACING.sm;
    const listH = Math.max(160, safe.y + safe.height - listTop - SPACING.lg);
    const list = new ScrollList(this, cx, listTop + listH / 2, { width: listW, height: listH });
    this.body.add(list);
    this.boxList = list;
    this.boxListWidth = listW - SPACING.md;
    // Wire keyboard nav to this single-column list.
    this.navList = list;
    this.navListViewH = listH;
    this.navCols = 1;
    this.refreshBoxList();
  }

  private teamList?: ScrollList;
  private boxList?: ScrollList;
  private boxListWidth = 400;

  /** All stored mons, flattened with their box name, matching the search query. */
  private storedMatches(): { mon: PokemonInstance; boxName: string }[] {
    const q = this.boxQuery.trim().toLowerCase();
    const out: { mon: PokemonInstance; boxName: string }[] = [];
    for (const box of this.session.getBoxes()) {
      for (const mon of box.contents) {
        const species = this.speciesOf(mon);
        const hay = `${this.displayName(mon, species)} ${species?.name ?? ''} ${
          species ? species.types.join(' ') : ''
        }`.toLowerCase();
        if (!q || hay.includes(q)) out.push({ mon, boxName: box.name });
      }
    }
    return out;
  }

  private refreshBoxList(): void {
    const list = this.boxList;
    if (!list) return;
    list.content.removeAll(true);
    // Rebuilding the box rows invalidates the keyboard cursor's cells.
    this.navCells = [];
    this.navList = list;
    this.navCols = 1;
    const matches = this.storedMatches();
    const w = this.boxListWidth;
    const rowH = 64;

    if (matches.length === 0) {
      const empty = this.add
        .text(
          0,
          0,
          this.boxQuery ? 'Nobody by that name in the boxes.' : 'Your boxes are empty for now.',
          {
            fontFamily: FONT.family,
            fontSize: `${FONT.size.sm}px`,
            color: CSS.textMuted,
          },
        )
        .setOrigin(0.5);
      // ScrollList maps content-local y=0 to the viewport TOP; place the empty line at
      // the top of the content (centered within the first row's worth of height).
      empty.y = rowH / 2;
      list.addItem(empty, rowH);
      list.setContentHeight(rowH);
      list.scrollTo(0);
      return;
    }

    // Lay rows out from the top down (content y=0 is the viewport TOP), so the first box
    // row sits at the top on open and the last is reachable by scrolling.
    let cursorY = SPACING.sm;
    for (const { mon, boxName } of matches) {
      const row = this.boxRow(mon, boxName, w, rowH);
      row.y = cursorY + rowH / 2;
      list.addItem(row, rowH + SPACING.xs);
      this.navCells.push({
        container: row,
        mon,
        source: 'box',
        cy: row.y,
        height: rowH,
        width: w,
      });
      cursorY += rowH + SPACING.xs;
    }
    list.setContentHeight(SPACING.sm + matches.length * (rowH + SPACING.xs) + SPACING.sm);
    list.scrollTo(0);
    // A live search re-seats the cursor onto the new first match (if the cursor was active).
    if (this.navCursor >= 0) {
      if (this.navCells.length > 0)
        this.setNavCursor(Math.min(this.navCursor, this.navCells.length - 1));
      else this.focusRing?.hide();
    }
  }

  private boxRow(
    mon: PokemonInstance,
    boxName: string,
    w: number,
    h: number,
  ): Phaser.GameObjects.Container {
    const species = this.speciesOf(mon);
    const panel = new Panel(this, 0, 0, { width: w, height: h, fill: COLORS.surface });
    const sprite = this.add.image(-w / 2 + SPACING.lg + 18, 0, '__MISSING').setVisible(false);
    if (species) void showSpecies(this, this.resolver, species.dexNo, sprite, 46);

    const tx = -w / 2 + SPACING.lg + 48;
    const name = this.add.text(tx, -h / 2 + SPACING.md, this.displayName(mon, species), {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.text,
      fontStyle: 'bold',
    });
    const sub = this.add.text(
      tx,
      -h / 2 + SPACING.md + FONT.size.md + 3,
      `Lv ${mon.level} · ${boxName}`,
      {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      },
    );

    const c = this.add.container(0, 0, [panel, sprite, name, sub]);

    if (species) {
      let rx = w / 2 - SPACING.md;
      for (let i = species.types.length - 1; i >= 0; i--) {
        const pill = typePill(this, 0, 0, species.types[i] as PokemonType);
        pill.x = rx - pill.width / 2;
        rx -= pill.width + SPACING.xs;
        c.add(pill);
      }
    }

    panel.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    panel.input!.cursor = 'pointer';
    // Open on TAP (POINTER_UP); a drag-scroll of the box list no-ops here.
    panel.on(Phaser.Input.Events.POINTER_UP, () => {
      if (!this.boxList?.wasDragging()) this.openDetail(mon, 'box');
    });
    return c;
  }

  // ── DETAIL overlay ──────────────────────────────────────────────────────────────

  private openDetail(mon: PokemonInstance, source: 'team' | 'box'): void {
    this.closeDetail();
    const safe = this.layout.safe;
    const w = Math.min(440, safe.width - SPACING.md * 2);
    const h = Math.min(safe.height - SPACING.lg * 2, 560);
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    const overlay = this.add.container(0, 0);
    overlay.setDepth(100);

    // Scrim — tap outside to close.
    const scrim = this.add.graphics();
    scrim.fillStyle(COLORS.scrim, 0.6);
    scrim.fillRect(0, 0, this.layout.width, this.layout.height);
    scrim.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, this.layout.width, this.layout.height),
      Phaser.Geom.Rectangle.Contains,
    );
    scrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeDetail());
    overlay.add(scrim);

    const panel = new Panel(this, cx, cy, {
      width: w,
      height: h,
      fill: COLORS.surface,
      shadow: true,
    });
    // Swallow taps on the card so they don't fall through to the scrim.
    panel.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    overlay.add(panel);

    const card = this.add.container(cx, cy);
    overlay.add(card);
    this.fillDetail(card, mon, source, w, h);

    this.detail = overlay;
    if (!this.reduced) {
      overlay.setAlpha(0);
      this.tweens.add({ targets: overlay, alpha: 1, duration: 140, ease: 'Sine.easeOut' });
    }
  }

  private fillDetail(
    card: Phaser.GameObjects.Container,
    mon: PokemonInstance,
    source: 'team' | 'box',
    w: number,
    h: number,
  ): void {
    const species = this.speciesOf(mon);
    const innerW = w - SPACING.lg * 2;
    const left = -w / 2 + SPACING.lg;
    const headerY = -h / 2 + SPACING.lg;

    const sprite = this.add.image(left + 36, headerY + 30, '__MISSING').setVisible(false);
    if (species) void showSpecies(this, this.resolver, species.dexNo, sprite, 76);
    card.add(sprite);

    const name = this.add.text(left + 84, headerY, this.displayName(mon, species), {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.lg}px`,
      color: CSS.text,
      fontStyle: 'bold',
    });
    card.add(name);
    const meta = this.add.text(
      left + 84,
      headerY + FONT.size.lg + 4,
      `${species?.name ?? mon.speciesId} · Lv ${mon.level}${mon.shiny ? ' · ✦ shiny' : ''}`,
      { fontFamily: FONT.family, fontSize: `${FONT.size.sm}px`, color: CSS.textMuted },
    );
    card.add(meta);

    if (species) {
      let tx = left + 84;
      for (const t of species.types) {
        const pill = typePill(
          this,
          0,
          headerY + FONT.size.lg + FONT.size.sm + 12,
          t as PokemonType,
        );
        pill.x = tx + pill.width / 2;
        tx += pill.width + SPACING.xs;
        card.add(pill);
      }
    }

    // The body (nature, XP, stats, moves) is a VARIABLE-LENGTH block: on a narrow phone
    // the nature line wraps and the whole stack can exceed a short detail panel, running
    // over the action buttons + the panel border. Put it in a touch-drag ScrollList that
    // fills the gap between the header and the action row, so it NEVER overlaps either —
    // it just scrolls. (We compute the action row first to know the body's bottom bound.)
    const actionY = h / 2 - SPACING.lg - 12;
    const btnH = Math.max(48, FONT.size.md + SPACING.lg);
    // For the team tab there are TWO extra rows above the action row: "Care for {name}" and
    // "Walk with me". Reserve their height so the scrolling body never overlaps them.
    const rowGap = SPACING.xl + 8;
    const actionRowTop = source === 'team' ? actionY - rowGap * 2 - btnH / 2 : actionY - btnH / 2;

    // The body band runs from just below the header to just above the action row, in
    // panel-local coords (the card container is centered on the panel center).
    const bandTop = headerY + 76;
    const bandBottom = actionRowTop - SPACING.md;
    const bandH = Math.max(48, bandBottom - bandTop);
    const list = new ScrollList(this, 0, bandTop + bandH / 2, {
      width: innerW + SPACING.md,
      height: bandH,
    });
    card.add(list);

    // Lay the body out in the list's CONTENT (content-local y=0 maps to the viewport top).
    let y = SPACING.xs;
    const cLeft = -innerW / 2;

    // Nature + ability line.
    const natureName = this.natureName(mon.natureId);
    const ability = this.abilityLabel(species, mon.abilityId);
    const trait = this.add.text(cLeft, y, `${natureName} nature · ${ability}`, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.textMuted,
      wordWrap: { width: innerW },
    });
    list.content.add(trait);
    y += trait.height + SPACING.sm;

    // XP-to-next, gentle.
    if (species) {
      const within = this.expWithinLevel(mon, species);
      if (within) {
        const xpBarW = innerW * 0.6;
        const g = this.add.graphics();
        g.fillStyle(COLORS.surfaceAlt, 1);
        g.fillRoundedRect(cLeft, y, xpBarW, 6, 3);
        g.fillStyle(COLORS.accentDeep, 1);
        g.fillRoundedRect(cLeft, y, Math.max(2, xpBarW * within.frac), 6, 3);
        list.content.add(g);
        const xpLabel = this.add.text(cLeft + xpBarW + SPACING.sm, y - 4, within.label, {
          fontFamily: FONT.mono,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textFaint,
        });
        list.content.add(xpLabel);
        y += SPACING.lg;
      }
    }

    // Computed stats grid.
    y += SPACING.sm;
    const stats = this.computedStats(mon, species);
    const maxStat = Math.max(1, ...STAT_ORDER.map((k) => stats[k]));
    for (const key of STAT_ORDER) {
      const label = this.add.text(cLeft, y, STAT_LABEL[key], {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      });
      const value = this.add
        .text(cLeft + 92 + innerW * 0.42 + SPACING.sm, y, `${stats[key]}`, {
          fontFamily: FONT.mono,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.text,
        })
        .setOrigin(0, 0);
      const barW = innerW * 0.42;
      const g = this.add.graphics();
      g.fillStyle(COLORS.surfaceAlt, 1);
      g.fillRoundedRect(cLeft + 92, y + 3, barW, 6, 3);
      g.fillStyle(COLORS.accent, 0.9);
      g.fillRoundedRect(cLeft + 92, y + 3, Math.max(2, barW * (stats[key] / maxStat)), 6, 3);
      list.content.add([label, g, value]);
      y += FONT.size.sm + SPACING.sm;
    }

    // Moves + PP.
    y += SPACING.sm;
    const movesHeading = this.add.text(cLeft, y, 'Moves', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.accent,
      fontStyle: 'bold',
    });
    list.content.add(movesHeading);
    y += FONT.size.sm + SPACING.sm;
    for (const slot of mon.moves) {
      const move = this.session.bundle.index.moveById(slot.moveId);
      const mName = this.add.text(cLeft, y, move?.name ?? slot.moveId, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.text,
      });
      const pp = this.add
        .text(cLeft + innerW, y, `PP ${slot.pp}/${slot.maxPp}`, {
          fontFamily: FONT.mono,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textFaint,
        })
        .setOrigin(1, 0);
      list.content.add([mName, pp]);
      y += FONT.size.sm + SPACING.xs + 2;
    }
    list.setContentHeight(y + SPACING.sm);
    list.scrollTo(0);
    const close = new Button(this, 0, actionY, {
      label: 'Close',
      variant: 'ghost',
      width: 120,
      reducedMotion: this.reduced,
      onClick: () => this.closeDetail(),
    });
    card.add(close);

    if (source === 'team') {
      const already = this.isFollower(mon);
      // "Care for {name}" — open the cozy Care room for this partner (benched/boxed mons are
      // welcome too, but this button lives on the TEAM detail per the MVP scope). Warm, never
      // gated. Returns here (team tab) on Back.
      const careBtn = new Button(this, 0, actionY - rowGap * 2, {
        label: `♥ Care for ${this.displayName(mon, species)}`,
        variant: 'warm',
        width: Math.min(innerW, 240),
        reducedMotion: this.reduced,
        onClick: () => this.openCare(mon),
      });
      card.add(careBtn);

      // "Walk with me" — set this team mon as the overworld follower. When this one
      // already trails the player it reads as a calm, disabled confirmation.
      const followBtn = new Button(this, 0, actionY - rowGap, {
        label: already ? '✿ Walking with you' : 'Walk with me',
        variant: already ? 'ghost' : 'primary',
        width: Math.min(innerW, 240),
        reducedMotion: this.reduced,
        onClick: () => void this.chooseFollower(mon),
      });
      followBtn.setDisabled(already);
      card.add(followBtn);

      const canDeposit = this.session.getTeam().length > 1;
      const move = new Button(this, 0, actionY, {
        label: 'To a box',
        variant: 'warm',
        width: 140,
        reducedMotion: this.reduced,
        onClick: () => void this.depositFromTeam(mon),
      });
      move.setDisabled(!canDeposit);
      close.x = -SPACING.sm - 70;
      move.x = SPACING.sm + 80;
      card.add(move);
    } else {
      const canWithdraw = this.session.getTeam().length < 6;
      const move = new Button(this, 0, actionY, {
        label: 'To my team',
        variant: 'primary',
        width: 150,
        reducedMotion: this.reduced,
        onClick: () => void this.withdrawToTeam(mon),
      });
      move.setDisabled(!canWithdraw);
      close.x = -SPACING.sm - 70;
      move.x = SPACING.sm + 85;
      card.add(move);
    }
  }

  private closeDetail(): void {
    this.detail?.destroy();
    this.detail = undefined;
  }

  /** Open the cozy Care room for a mon, returning to the team tab on Back. */
  private openCare(mon: PokemonInstance): void {
    this.closeDetail();
    goTo(this, 'Care', { data: { instanceId: mon.id, returnScene: 'TeamBoxes' } });
  }

  // ── mutations (session owns the rules; we present + persist) ─────────────────────

  private async depositFromTeam(mon: PokemonInstance): Promise<void> {
    this.session.moveTeamToBox(mon.id);
    await this.session.save();
    this.closeDetail();
    this.renderTab();
    this.toast(`${this.displayName(mon, this.speciesOf(mon))} is resting in a box.`);
  }

  private async withdrawToTeam(mon: PokemonInstance): Promise<void> {
    const ok = this.session.moveBoxToTeam(mon.id);
    await this.session.save();
    this.closeDetail();
    this.renderTab();
    if (ok) this.toast(`${this.displayName(mon, this.speciesOf(mon))} joined your team.`);
  }

  /** Set this team mon as the overworld follower (session persists). */
  private async chooseFollower(mon: PokemonInstance): Promise<void> {
    await this.session.setFollower(mon.speciesId);
    this.closeDetail();
    this.renderTab();
    this.toast(`${this.displayName(mon, this.speciesOf(mon))} will walk with you.`);
  }

  /** Whether `mon` is the team member currently following the player in the overworld. */
  private isFollower(mon: PokemonInstance): boolean {
    const follower = this.session.follower;
    return follower != null && follower.id === mon.id;
  }

  private toast(text: string): void {
    const safe = this.layout.safe;
    new Toast(this, this.layout.centerX, safe.y + safe.height - SPACING.xxl, {
      text,
      width: Math.min(360, safe.width - SPACING.lg * 2),
      reducedMotion: this.reduced,
    });
  }

  // ── data helpers ────────────────────────────────────────────────────────────────

  private speciesOf(mon: PokemonInstance): SpeciesData | undefined {
    return this.session.bundle.index.speciesById(mon.speciesId);
  }

  private displayName(mon: PokemonInstance, species?: SpeciesData): string {
    return mon.nickname && mon.nickname.length > 0
      ? mon.nickname
      : (species?.name ?? mon.speciesId);
  }

  private natureName(id: string): string {
    return NATURES.find((n) => n.id === id)?.name ?? id;
  }

  private abilityLabel(species: SpeciesData | undefined, abilityId: string): string {
    if (!species) return abilityId;
    const slots = species.abilities;
    const hidden = slots.hidden === abilityId;
    const human = abilityId
      .split('-')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
    return hidden ? `${human} (hidden)` : human;
  }

  private computedStats(mon: PokemonInstance, species?: SpeciesData): Record<string, number> {
    if (!species) {
      return { hp: mon.maxHp, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };
    }
    let nature;
    try {
      nature = getNature(mon.natureId);
    } catch {
      nature = NATURES[0];
    }
    return computeStats(species.baseStats, mon.ivs, mon.evs, mon.level, nature);
  }

  /** XP progress within the current level (frac + "cur/next" label), or null at cap. */
  private expWithinLevel(
    mon: PokemonInstance,
    species: SpeciesData,
  ): { frac: number; label: string } | null {
    const rate = species.growthRate;
    const cur = expForLevel(rate, mon.level);
    const next = expForLevel(rate, mon.level + 1);
    if (next <= cur) return null;
    const into = Math.max(0, mon.exp - cur);
    const span = next - cur;
    return { frac: Math.max(0, Math.min(1, into / span)), label: `${into}/${span} to next` };
  }

  // ── layout ──────────────────────────────────────────────────────────────────────

  private positionBody(): void {
    // The body container holds absolutely-positioned children; nothing to move here,
    // but kept as a hook so a tab swap re-runs after relayout if needed.
  }

  private relayout(): void {
    redrawBackdrop(this.backdrop, this.layout);
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.md;

    const backW = this.backButton?.buttonWidth ?? 110;
    const backH = this.backButton?.buttonHeight ?? 48;
    const headingW = this.heading?.width ?? 0;
    const headingH = this.heading?.height ?? 24;

    // The header would crowd on a narrow phone: a centered heading collides with the
    // left back button, and a single-row tab strip then overlaps both. So on a COMPACT
    // viewport we STACK the header — back button alone on row 1, heading centered on row
    // 2, the tab strip on row 3 — guaranteeing nothing overlaps on ~390px. A roomy
    // desktop keeps the original one-row header (back left, heading centered, tabs below).
    const backCx = safe.x + SPACING.md + backW / 2;
    // Would a centered heading overlap the back button on this width? (Only relevant on
    // the shared single-row path.)
    const headingClearsBack = cx - headingW / 2 >= backCx + backW / 2 + SPACING.sm;
    const stacked = this.layout.isCompact || !headingClearsBack;

    const tabW = 120;
    const tabH = this.tabButtons[0]?.button.buttonHeight ?? 48;
    const totalTabW = tabW * 2 + SPACING.sm;

    let tabY: number;
    if (stacked) {
      // Row 1: back button, top-left.
      const backY = top + backH / 2;
      this.backButton?.setPosition(backCx, backY);
      // Row 2: heading, centered, clear below the back button.
      const headingY = backY + backH / 2 + SPACING.sm + headingH / 2;
      this.heading?.setPosition(cx, headingY);
      // Row 3: tab strip, centered, clear below the heading.
      tabY = headingY + headingH / 2 + SPACING.md + tabH / 2;
    } else {
      // One-row header: back left + heading centered share a row; tabs sit below.
      const rowY = top + Math.max(backH, headingH) / 2;
      this.backButton?.setPosition(backCx, rowY);
      this.heading?.setPosition(cx, rowY);
      tabY = rowY + Math.max(backH, headingH) / 2 + SPACING.md + tabH / 2;
    }

    let tx = cx - totalTabW / 2 + tabW / 2;
    for (const { button } of this.tabButtons) {
      button.setPosition(tx, tabY);
      tx += tabW + SPACING.sm;
    }

    // Tell the body where to start — cleanly below the tab strip in either layout.
    this.headerBottom = tabY + tabH / 2 + SPACING.md;

    // Rebuild the body so cards reflow to the new width.
    this.closeDetail();
    this.renderTab();
  }
}
